import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Board, { moveCard } from "@lourenci/react-kanban";
import KanbanCard from "./card/KanbanCard";
import KanbanColumn from "./KanbanColumn";
import FunctionBanner from "../FunctionBanner";
import Button from '@mui/material-next/Button';
import { FormControlLabel, IconButton, Tooltip } from "@mui/material";
import KanbanRatingButton from "./KanbanRatingButton";
import { ArrowBackRounded } from "@material-ui/icons";
import { Checkbox } from "@mui/material";
import DomainTwoToneIcon from '@mui/icons-material/DomainTwoTone';
import KanbanAccessDialog from "./dialogs/KanbanAccessDialog";
import { useTranslation } from "react-i18next";
import { useNotifications } from "../../hooks/notification/notificationHook";
import GroupTwoToneIcon from '@mui/icons-material/GroupTwoTone';
import MarkEmailReadTwoToneIcon from '@mui/icons-material/MarkEmailReadTwoTone';
import EventAvailableTwoToneIcon from '@mui/icons-material/EventAvailableTwoTone';
import FilterAltTwoToneIcon from '@mui/icons-material/FilterAltTwoTone';
import KanbanAdvancedFilterDialog from "./dialogs/KanbanAdvancedFilterDialog";
import { UserContext } from "../../context/UserContext"
import BlurryBackdrop from "../../widget/BlurryBackdrop";
import ApiClient from "../../api/ApiClient";
import PromiseBuilder from "../../utils/PromiseUtils";
import KanbanConfirmCampaingEndDialog from "./dialogs/KanbanConfirmCampaingEndDialog";
import { formsToFilterBuilder, buildFilterForm, gatherForms } from '../../utils/FilterUtils';
import { ratings, initialBoard, candidatesDtoToBoard, columnIdToBoardState } from '../../utils/KanbanMapper';
import { campaignDtoToJ, campaignJToDto } from '../../utils/CampaignMapper';
import KanbanCommentDialog from "./dialogs/KanbanCommentDialog";
import ExcelExport from "../../utils/ExcelExport";
import KanbanConfirmRejectionDialog from "./dialogs/KanbanConfirmRejectionDialog";

const CAMPAIGN_FILTER_STORAGE_KEY = 'jobbit-campaign-filter-';
const CAMPAIGN_SHOW_REJECTION_POPUP_STORAGE_KEY = 'jobbit-campaign-rejection-';
const initialSelectedComment = {id: "", comment: ""};

function getFilterForm(campaignId, numberOfSegments) {
    const ls = localStorage.getItem(CAMPAIGN_FILTER_STORAGE_KEY + campaignId);
    if (ls) {
        try {
            return JSON.parse(ls);
        } catch (er) {
            console.error(er);
        }
    }
    const filterForm = buildFilterForm(numberOfSegments);
    return filterForm;
}

function storeForm(campaignId, filterForm) {
    localStorage.setItem(CAMPAIGN_FILTER_STORAGE_KEY + campaignId, JSON.stringify(filterForm));
}

function storeShowRejectionPopup(campaignId) {
    const item = {
        show: false
    };
    localStorage.setItem(CAMPAIGN_SHOW_REJECTION_POPUP_STORAGE_KEY + campaignId, JSON.stringify(item));
}

function getShowRejectionPopup(campaignId) {
    const ls = localStorage.getItem(CAMPAIGN_SHOW_REJECTION_POPUP_STORAGE_KEY + campaignId);
    if (ls) {
        return true;
    }
    return false;
}

function ControlledBoard() {

    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { addSuccessNotification, addErrorNotification } = useNotifications();
    const { user } = useContext(UserContext);
    const [campaign, setCampaign] = useState();

    const { id } = useParams()

    const [filteredBoard, setFilteredBoard] = useState(initialBoard);
    const [selectedRatings, setSelectedRatings] = useState([true, true, true, true, true, true]);
    const [kanbanAccessDialogOpen, setKanbanAccessDialogOpen] = useState(false);
    const [kanbanAdvancedFilterDialogOpen, setKanbanAdvancedFilterDialogOpen] = useState(false);
    const [kanbanConfirmCampaingEndDialogOpen, setKanbanConfirmCampaingEndDialogOpen] = useState(false);
    const [kanbanCommentDialogOpen, setKanbanCommentDialogOpen] = useState(false);
    const [kanbanConfirmRejectionDialogOpen, setKanbanConfirmRejectionDialogOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [selectedCards, setSelectedCards] = useState([]);
    const [selectedComment, setSelectedComment] = useState(initialSelectedComment);
    const [isLoading, setIsLoading] = useState(false);
    const [filterForm, setFilterForm] = useState();
    const [formBuilder, setFormBuilder] = useState({ segments: [] });

    const [skipRejectionWarningPopup, setSkipRejectionWarningPopup] = useState(getShowRejectionPopup(id));

    const [candidateToReject, setCandidateToReject] = useState(null);

    const [openQuestionableColumn, setOpenQuestionableColumn] = useState(false);

    const toggleQuestionableColumn = () => {
        const newValue = !openQuestionableColumn;
        setOpenQuestionableColumn(newValue);
        if (newValue) {
            var elements = document.querySelectorAll('.react-kanban-column');
            for (var i = 0; i < elements.length; i++) {
                elements[i].classList.add('react-kanban-column-extended');
                elements[i].classList.remove('react-kanban-column');

            }
        } else {
            var elements = document.querySelectorAll('.react-kanban-column-extended');
            for (var i = 0; i < elements.length; i++) {
                elements[i].classList.add('react-kanban-column');
                elements[i].classList.remove('react-kanban-column-extended');
            }
        }
    }

    useEffect(() => {
        fetchCandidateData();
        if (location.state) {
            setCampaign(location.state);
        } else {
            fetchCampaignData();
        }
        toggleQuestionableColumn();
    }, [id]);

    const toggleRating = (ratingIndex) => {
        let newArr = [...selectedRatings]; 
        newArr[ratingIndex] = !newArr[ratingIndex];
        setSelectedRatings(newArr);
    }

    function updateCardStatus(_card, source, destination) {
        setIsLoading(true);
        const payload = {
            campaignId: id,
            candidate: {
                email: _card.id,
                boardState: columnIdToBoardState(destination.toColumnId),
                rank: _card.rating
            }
        };
        new PromiseBuilder()
            .then(async () => ApiClient.put("/api/v1/campaign/candidate", payload))
            .then(async (res) => {
                setIsLoading(false);
                var updatedBoard = moveCard(filteredBoard, source, destination);
                updatedBoard.columns[destination.toColumnId].cards[destination.toPosition].columnId = destination.toColumnId;
                setFilteredBoard(updatedBoard);
                // When a card is moved to 'Called', the comment dialog is opened by default
                if (destination.toColumnId == 4) {
                    openKanbanCommentDialog(_card.id, destination.toColumnId);
                }
            })
            .catch(async (e) => {
                setIsLoading(false);
                addErrorNotification(t("toast.updatingCandidateFailed"), t("toast.error"));
                console.log("ERROR", e);
            })
            .run();
    }

    const fetchCampaignData = () => {
        setIsLoading(true);
        new PromiseBuilder()
            .then(async () => ApiClient.get("/api/v1/campaign/" + id))
            .then(async (res) => {
                setIsLoading(false);
                const camp = campaignDtoToJ(res.data.campaign);
                setCampaign(camp);
            })
            .catch(async (e) => {
                setIsLoading(false);
                addErrorNotification(t("toast.fetchCampaignsFailed"), t("toast.error"));
                console.log("ERROR", e);
            })
            .run();
    }

    const updateCardRating = (cardId, columnId, rating) => {
        setIsLoading(true);
        const payload = {
            campaignId: id,
            candidate: {
                email: cardId,
                boardState: columnIdToBoardState(columnId),
                rank: rating
            }
        };
        new PromiseBuilder()
            .then(async () => ApiClient.put("/api/v1/campaign/candidate", payload))
            .then(async (res) => {
                setIsLoading(false);
                let newBoard = JSON.parse(JSON.stringify(filteredBoard));
                for (let i = 0; i < newBoard.columns.length; i++) {
                    for (let j = 0; j < newBoard.columns[i].cards.length; j++) {

                        if (newBoard.columns[i].cards[j].id == cardId) {
                            newBoard.columns[i].cards[j].rating = rating;
                            break;
                        }
                    }
                }
                setFilteredBoard(newBoard);
            })
            .catch(async (e) => {
                setIsLoading(false);
                addErrorNotification(t("toast.updatingCandidateFailed"), t("toast.error"));
                console.log("ERROR", e);
            })
            .run();
    }

    const endCampaign = () => {
        setIsLoading(true);
        new PromiseBuilder()
            .then(async () => ApiClient.delete("/api/v1/campaign/" + id))
            .then(async (res) => {
                setIsLoading(false);
                addSuccessNotification(t("toast.campaignSuccessfullyClosed", t("toast.success")));
                navigate("/");
            })
            .catch(async (e) => {
                setIsLoading(false);
                addErrorNotification(t("toast.savingCampaignFailed"), t("toast.error"));
                console.log("ERROR", e);
            })
            .run();
    }

    const fetchCandidateData = () => {
        setIsLoading(true);
        new PromiseBuilder()
            .then(async () => ApiClient.get("/api/v1/campaign/" + id + "/candidate"))
            .then(async (res) => {
                setIsLoading(false);
                const fb = candidatesDtoToBoard(res.data.candidates);
                setFilteredBoard(fb);
                var forms = gatherForms(fb);
                if (forms.length > 0) {
                    const filterForm = getFilterForm(id, forms[0].segments.length);
                    setFilterForm(filterForm);
                    const formBuilder = formsToFilterBuilder(forms);
                    
                    setFormBuilder(formBuilder);
                }
            })
            .catch(async (e) => {
                setIsLoading(false);
                addErrorNotification(t("toast.fetchCandidatesFailed"), t("toast.error"));
                console.log("ERROR", e);
            })
            .run();
    }

    const saveCardComment = (cardId, columnId, comment, commentId, resetForm) => {
        const payload = {
            campaignId: id,
            email: selectedCard.cardId,
            comment: comment,
            commentId: commentId
        }
        setIsLoading(true);

        if (commentId == "") {
            new PromiseBuilder()
                .then(async () => ApiClient.post("/api/v1/campaign/candidate/comment", payload))
                .then(async (res) => {
                    setIsLoading(false);
                    let newBoard = JSON.parse(JSON.stringify(filteredBoard));
                    var column = newBoard.columns.find((column) => column.id == columnId);
                    var card = column.cards.find((card) => card.id == cardId);
                    const commentEntry = {
                        email: user.email,
                        createdAt: new Date(),
                        content: comment
                    }
                    card.comments.push(commentEntry);
                    setFilteredBoard(newBoard);
                    closeKanbanCommentDialog();
                    resetForm();
                })
                .catch(async (e) => {
                    setIsLoading(false);
                    addErrorNotification(t("toast.addingCommentFailed"), t("toast.error"));
                    console.log("ERROR", e);
                })
                .run();
        } else {
            new PromiseBuilder()
                .then(async () => ApiClient.put("/api/v1/campaign/candidate/comment/" + commentId, payload))
                .then(async (res) => {
                    setIsLoading(false);
                    fetchCandidateData();
                    closeKanbanCommentDialog();
                    resetForm();
                })
                .catch(async (e) => {
                    setIsLoading(false);
                    addErrorNotification(t("toast.updatingCommentFailed"), t("toast.error"));
                    console.log("ERROR", e);
                })
                .run();
        }
    }

    const deleteComment = (commentId) => {
        new PromiseBuilder()
                .then(async () => ApiClient.delete("/api/v1/campaign/candidate/comment/" + commentId))
                .then(async (res) => {
                    setIsLoading(false);
                    fetchCandidateData();
                })
                .catch(async (e) => {
                    setIsLoading(false);
                    addErrorNotification(t("toast.deletingCommentFailed"), t("toast.error"));
                    console.log("ERROR", e);
                })
                .run();
    }

    const saveCampaignRecruiters = (recruiters, resetForm) => {
        setIsLoading(true);
        const campaignToUpdate = JSON.parse(JSON.stringify(campaign));
        campaignToUpdate.recruiters = recruiters;
        const campaignDto = campaignJToDto(campaignToUpdate);
      
        new PromiseBuilder()
        .then(async () =>
            ApiClient.put("/api/v1/campaign", campaignDto)
        )
        .then(async (res) => {
            closeKanbanAccessDialog(resetForm);
            fetchCampaignData();
            setIsLoading(false);
            
        })
        .catch(async (e) => {
            setIsLoading(false);
            addErrorNotification(t("toast.savingCampaignFailed"), t("toast.error"));
            console.log("ERROR", e);
        })
        .run();

        closeKanbanAccessDialog(resetForm);
    }

    const handleReject = (candidateId, skipRejectionPopup) => {
        new PromiseBuilder()
        .then(async () =>
            ApiClient.post("/api/v1/campaign/" + id + "/candidate/" + candidateId + "/reject")
        )
        .then(async (res) => {
            fetchCandidateData();
            setIsLoading(false);
            addSuccessNotification(t("toast.rejectingEmailSent", t("toast.success")));

        })
        .catch(async (e) => {
            setIsLoading(false);
            addErrorNotification(t("toast.rejectingEmailFailed"), t("toast.error"));
            console.log("ERROR", e);
        })
        .run();
        setCandidateToReject(null);
        if (skipRejectionPopup) {
            storeShowRejectionPopup(id);
        }
        setSkipRejectionWarningPopup(skipRejectionPopup);
        setKanbanConfirmRejectionDialogOpen(false);
    }

    const closeKanbanAccessDialog = ( resetForm ) => {
        resetForm();
        setKanbanAccessDialogOpen(false);
    }

    const closeKanbanAdvancedFilterDialog = () => {
        setKanbanAdvancedFilterDialogOpen(false);
    }

    const closeKanbanConfirmCampaingEndDialog = () => {
        setKanbanConfirmCampaingEndDialogOpen(false);
    }

    const closeKanbanConfirmRejectionDialog = () => {
        setKanbanConfirmRejectionDialogOpen(false);
        setCandidateToReject(null);
    }

    const handleKanbanConfirmRejectionDialog = (candidateId) => {
        if (!skipRejectionWarningPopup) {
            setCandidateToReject(candidateId);
            setKanbanConfirmRejectionDialogOpen(true);
        } else {
            handleReject(candidateId, skipRejectionWarningPopup);
        }
    }

    const openKanbanCommentDialog = (cardId, columnId) => {
        setKanbanCommentDialogOpen(true);
        setSelectedCard({ cardId: cardId, columnId: columnId});
    }

    const closeKanbanCommentDialog = () => {
        setKanbanCommentDialogOpen(false);
        setSelectedCard(null)
        setSelectedComment(initialSelectedComment);
    }

    const editComment = (cardId, columnId, commentId, comment) => {
        setKanbanCommentDialogOpen(true);
        setSelectedCard({ cardId: cardId, columnId: columnId});
        setSelectedComment({id: commentId, comment: comment});
    }

    const toggleSelectedCard = (cardId) => {
        if (selectedCards.includes(cardId)) {
            setSelectedCards(selectedCards.filter(e => e !== cardId))
        } else {
            let newSelectedCards = JSON.parse(JSON.stringify(selectedCards));
            newSelectedCards.push(cardId);
            setSelectedCards(newSelectedCards);
        }
    }

    const copyToClipBoard = async () => {
        try {
            let emails = "";
            for (let i = 0; i < filteredBoard.columns.length; i++) {
                for (let j = 0; j < filteredBoard.columns[i].cards.length; j++) {
                    if (selectedCards.includes(filteredBoard.columns[i].cards[j].id)) {
                        emails = emails + filteredBoard.columns[i].cards[j].email + "; ";
                    }
                }
            }
            await navigator.clipboard.writeText(emails);
            addSuccessNotification(t("copiedToClipboard"), t("toast.success"))
            setSelectedCards([]);
        } catch (err) {
            alert(err);
            setCopySuccess('Failed to copy!');
        }
    };

    useEffect(() => {
        let newBoard = JSON.parse(JSON.stringify(filteredBoard));
        for (let i = 0; i < newBoard.columns.length; i++) {
            for (let j = 0; j < newBoard.columns[i].cards.length; j++) {
                newBoard.columns[i].cards[j].isCardSelected = false;
                if (selectedCards.includes(newBoard.columns[i].cards[j].id)) {
                    newBoard.columns[i].cards[j].isCardSelected = true;
                }
            }
        }
        setFilteredBoard(newBoard);
    }, [selectedCards]);

    useEffect(() => {
        if (filterForm) {
            
            let newBoard = JSON.parse(JSON.stringify(filteredBoard));

            // BOARD COLUMN LOOP
            for (let i = 0; i < newBoard.columns.length; i++) {
                // BOARD COLUMN ROW LOOP (CARD)
                for (let j = 0; j < newBoard.columns[i].cards.length; j++) {
                    const card = newBoard.columns[i].cards[j];
                    card.isCardVisible = true;
                    if (!selectedRatings[card.rating]) {
                        card.isCardVisible = false;
                    } else {
                        for (let seg = 0; seg < card.form.segments.length; seg++) {
                            var formSegment = card.form.segments[seg];
                            var filterSegment = filterForm[seg];
                            var builderSegment = formBuilder.segments[seg];
                            const checkIntersect = (filterSegment, formSegment) => {
                            
                                const type = formSegment.type;
                                const filterArray = type === "radial" ? filterSegment.radial : filterSegment.freeText;
                                if (filterArray.length == 0) {
                                    // If no filters are set, everything is visible
                                    return true;
                                }

                                const answerArray = formSegment.answers
                                    .filter(item => item.selected && item.type == type)
                                    .map(item => item.answer);

                                    // STARI 'AND' APPROACH
                                /*if (builderSegment.multiselect) {
                                    return filterArray.every(filterElement => {
                                        const c = answerArray.includes(filterElement)
                                        return c;
                                    })
                                } else {
                                    return filterArray.some(filterElement => {
                                        const c = answerArray.includes(filterElement)
                                        return c;
                                    })
                                }*/

                                return filterArray.some(filterElement => {
                                    const c = answerArray.includes(filterElement)
                                    return c;
                                })
                            }
                            
                            const cardVisible = checkIntersect(filterSegment, formSegment);
                            card.isCardVisible = cardVisible;

                            if(!cardVisible) {
                                break;
                            }
                        }
                    }
                }
            }
           
            setFilteredBoard(newBoard);
        }

    }, [filterForm, selectedRatings]);

    const handleFilterChange = () => {
        setKanbanAdvancedFilterDialogOpen(false);
        storeForm(id, filterForm);
    }

    return (
        <>
            <BlurryBackdrop
                open={isLoading}
            />

            <KanbanCommentDialog
                cardId={selectedCard?.cardId}
                columnId={selectedCard?.columnId}
                open={kanbanCommentDialogOpen}
                handleClose={closeKanbanCommentDialog}
                handleSave={saveCardComment}
                cardComment={selectedComment.comment}
                cardCommentId={selectedComment.id}
            />

            <KanbanConfirmCampaingEndDialog
                campaignId={id}
                open={kanbanConfirmCampaingEndDialogOpen}
                handleClose={closeKanbanConfirmCampaingEndDialog}
                handleSubmit={() => endCampaign()}
            />

            <KanbanConfirmRejectionDialog
                candidateId={candidateToReject}
                open={kanbanConfirmRejectionDialogOpen}
                handleClose={closeKanbanConfirmRejectionDialog}
                handleReject={handleReject}
                dontShowAgain={skipRejectionWarningPopup}
                
            />

            <div className="kanban" style={{ paddingTop: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <FunctionBanner>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                        <Tooltip title={t('kanban.advancedFilter')}>
                            <Button
                                variant="outlined"
                                size="small"
                                className={"kanban color color-white is-rating-button is-rating-button-filter"}
                                style={{ fontWeight: '800', boxShadow: 'none', borderRadius: '10px', marginRight: '10px' }}
                                onClick={() => {
                                    setKanbanAdvancedFilterDialogOpen(true);
                                }}
                            >
                                <FilterAltTwoToneIcon className="color color-medium-purple" />
                            </Button>
                        </Tooltip>
                        {ratings.map((rating, index) => (
                            <KanbanRatingButton
                                key={index}
                                id={index}
                                type={rating}
                                index={index}
                                toggleRating={toggleRating}
                                isActive={selectedRatings[index]}
                            />
                        ))}

                    </div>
                  
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                           
                            {(user.isAdmin || user.isManager) && (
                            <>
                                <ExcelExport data={filteredBoard} fileName={campaign?.campaign} />
                                <Tooltip title={t('campaign.endCampaign')}>
                                    <IconButton
                                        size="large"
                                        style={{ marginRight: '5px' }}
                                        onClick={() => {
                                            setKanbanConfirmCampaingEndDialogOpen(true);
                                        }}
                                    >
                                        <EventAvailableTwoToneIcon
                                            fontSize="large"
                                            className="color color-medium-red"
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('campaign.manageCampaign')}>
                                    <IconButton
                                        size="large"
                                        onClick={() => {
                                            setKanbanAccessDialogOpen(true);
                                        }}>
                                        <GroupTwoToneIcon
                                            fontSize="large"
                                            className="color color-medium-pink"
                                        />
                                    </IconButton>
                                </Tooltip>
                            </>
                            )}
                            <Tooltip title={t('campaign.copyEmails')}>
                                <span>
                                    <IconButton
                                        size="large"
                                        style={{ marginRight: '5px' }}
                                        disabled={selectedCards.length == 0}
                                        onClick={() => {
                                            copyToClipBoard();
                                        }}
                                    >
                                        <MarkEmailReadTwoToneIcon
                                            fontSize="large"
                                            disabled={selectedCards.length == 0}
                                            className={"color color-" + (selectedCards.length == 0 ? "light-gray" : 'medium-purple')}
                                        />

                                    </IconButton>
                                </span>
                            </Tooltip>
                        </div>
                </FunctionBanner>

                <KanbanAdvancedFilterDialog
                    filterForm={filterForm}
                    setFilterForm={setFilterForm}
                    formBuilder={formBuilder}
                    open={kanbanAdvancedFilterDialogOpen}
                    handleClose={closeKanbanAdvancedFilterDialog}
                    handleSubmit={handleFilterChange}
                />
                {((user.isAdmin || user.isManager) && (campaign && campaign.id)) && (
                    <KanbanAccessDialog
                        groupName={ campaign?.managers[0].group}
                        recruiters={campaign.recruiters}
                        open={kanbanAccessDialogOpen}
                        handleClose={closeKanbanAccessDialog}
                        handleSave={saveCampaignRecruiters}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                    />
                )}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'start' }}>
                        <IconButton
                            onClick={() => {
                                navigate("/");
                            }}>
                            <ArrowBackRounded fontSize="large" className="color color-medium-purple" />
                        </IconButton>
                        <div
                            className="color color-medium-purple"
                            style={{ fontSize: '28px', fontWeight: '800', marginRight: '10px' }}
                        >
                            {campaign?.campaign}
                        </div>
                        <DomainTwoToneIcon className="color color-dark-gray" style={{ marginRight: '10px' }} />
                        <div className="color color-dark-gray" style={{ fontSize: '18px', fontWeight: '600', height: '20px', marginRight: '10px' }}>
                            {campaign?.company}
                        </div>
                        <img

                            loading="lazy"
                            srcSet={`https://flagcdn.com/w40/${campaign?.country.code.toLowerCase()}.png 2x`}
                            src={`https://flagcdn.com/w20/${campaign?.country.code.toLowerCase()}.png`}
                            alt=""
                        />
                    </div>
                    {user.isAdmin && (
                        <FormControlLabel
                            value="start"
                            control={
                                <Checkbox
                                    checked={selectedCards.length > 0}
                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                                    onClick={() => {
                                        setSelectedCards([]);
                                    }}
                                    disabled={selectedCards.length == 0}
                                />
                            }
                            label={t('unselectAll')}
                            labelPlacement="start"
                        />
                    )}
                </div>
                <div className="sticky-kanban-headers" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', marginBottom: '10px' }}>
                
                {filteredBoard.columns.map((column, index) => {
                   
                   return (
                    <KanbanColumn
                        id={column.id}
                        key={column.id}
                        title={column.title}
                        columnName={column.column}
                        toggleQuestionableColumn={toggleQuestionableColumn}
                        openQuestionableColumn={openQuestionableColumn}
                        candidates={column.cards.length}
                    />  
                   )
                })}
                </div>
                <Board
                    onCardDragEnd={updateCardStatus}
                    disableColumnDrag
                    renderColumnHeader={({ id, title, column }) => (
                        <></>
                    )}
                    renderCard={({ id, fullName, email, phone, location, emailSent, rating, columnId, createdAt, comments, form, city, isCardVisible, isCardSelected }, { dragging }) => (
                        <KanbanCard
                            id={id}
                            fullName={fullName}
                            email={email}
                            phone={phone}
                            location={location}
                            emailSent={emailSent}
                            rating={rating}
                            createdAt={createdAt}
                            city={city}
                            comments={comments}
                            segments={form.segments}
                            updateCardRating={updateCardRating}
                            saveCardComment={saveCardComment}
                            openKanbanCommentDialog={openKanbanCommentDialog}
                            closeKanbanCommentDialog={closeKanbanCommentDialog}
                            kanbanCommentDialogOpen={kanbanCommentDialogOpen}
                            columnId={columnId}
                            isCardVisible={isCardVisible}
                            toggleSelectedCard={toggleSelectedCard}
                            isCardSelected={isCardSelected}
                            openQuestionableColumn={openQuestionableColumn}
                            isUserAdmin={user.isAdmin}
                            editComment={editComment}
                            deleteComment={deleteComment}
                            handleReject={handleKanbanConfirmRejectionDialog}
                        />
                    )}
                >
                    {filteredBoard}
                </Board>
            </div>
        </>
    );
}
const KanbanPage = () => {
    return (
        <>
            <ControlledBoard style={{minWidth: '1200px'}} />
        </>
    );
};

export default KanbanPage;